import {useState} from 'react';
import {Badge, Box, Button, Flex, Icon, Text, VStack} from '@chakra-ui/react';
import {
  FiArrowRight,
  FiChevronRight,
  FiClock,
  FiLock,
  FiPlus,
  FiUser,
  FiUsers,
} from '../../icons';
import usePlannerStyles from '../planner/usePlannerStyles';
import {RECAP_GRAY_SECONDARY_BUTTON_PROPS} from '../common/recapButtonStyles';
import type {OpenTelephoneEncounterSummary} from '../../types/telephoneEncounter';

// Format the "opened on" chip — date AND clock time, so the audit trail
// ("who opened this, when exactly") is visible at a glance. The backend may
// not always surface a date, so this returns '' when there's nothing useful
// to show (the caller omits the chip entirely rather than render
// "Invalid Date"). ECW sometimes returns a date-only value that parses to
// local midnight; a bare midnight is far more likely "no time recorded" than
// a real 12:00 AM call, so we omit the clock time for those.
function formatOpenedAt(value?: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const date = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const hasClockTime =
    d.getHours() !== 0 || d.getMinutes() !== 0 || d.getSeconds() !== 0;
  if (!hasClockTime) return date;
  const time = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${date}, ${time}`;
}

// The best free-text to preview for a row: the encounter message reads as the
// "what this is about" line; fall back to the latest notes / action taken so a
// row is never blank when ECW only populated one of the fields.
function previewText(match: OpenTelephoneEncounterSummary): string {
  return (
    String(match.message || '').trim() ||
    String(match.notes || '').trim() ||
    String(match.actionTaken || '').trim()
  );
}

// One selectable open-encounter card. The whole card is the click target (it
// reads as a list row), with a trailing chevron as the affordance and a hover
// lift so it's obviously interactive — same language as the planner schedule
// rows and history cards.
const OpenEncounterCard = ({
  match,
  isBusy,
  isPending,
  onEdit,
}: {
  match: OpenTelephoneEncounterSummary;
  isBusy: boolean;
  isPending: boolean;
  onEdit: () => void;
}) => {
  const styles = usePlannerStyles();
  const {panelBg, borderColor, textColor, subtleText, captionTextColor} =
    styles;

  const isAddressed = match.status === 'addressed' || match.locked === true;
  const title = String(match.reasonText || '').trim() || 'Telephone encounter';
  const preview = previewText(match);
  const openedAt = formatOpenedAt(match.createdAt);

  const metaItems = [
    match.providerName
      ? {key: 'provider', label: match.providerName, icon: FiUser}
      : null,
    match.assignedToName
      ? {
          key: 'assignee',
          label: `Assigned to ${match.assignedToName}`,
          icon: FiUsers,
        }
      : null,
    match.createdByName
      ? {key: 'opener', label: `Opened by ${match.createdByName}`, icon: null}
      : null,
    openedAt ? {key: 'date', label: openedAt, icon: FiClock} : null,
  ].filter(Boolean) as {key: string; label: string; icon: any}[];

  return (
    <Box
      as="button"
      type="button"
      onClick={onEdit}
      disabled={isBusy}
      textAlign="left"
      w="100%"
      bg={panelBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      px={4}
      py={3.5}
      transition="background 0.15s ease, border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease"
      _hover={
        isBusy
          ? undefined
          : {
              borderColor: styles.listRowSelectedBorderColor,
              bg: styles.listRowSelectedBg,
              transform: 'translateY(-1px)',
              boxShadow: styles.cardShadow,
            }
      }
      _focusVisible={{
        ...styles.focusOutlineStyles,
        bg: styles.listRowSelectedBg,
      }}
      _disabled={{opacity: 0.6, cursor: 'default'}}
      sx={{appearance: 'none'}}
    >
      <Flex align="flex-start" gap={3}>
        <VStack align="stretch" spacing={1.5} flex={1} minW={0}>
          <Flex align="center" gap={2} flexWrap="wrap">
            <Text
              fontSize="md"
              fontWeight="medium"
              color={textColor}
              lineHeight="1.3"
              noOfLines={1}
              minW={0}
            >
              {title}
            </Text>
            <Badge
              display="inline-flex"
              alignItems="center"
              gap={1}
              px={2}
              py={0.5}
              borderRadius="md"
              fontSize="xs"
              fontWeight="normal"
              textTransform="none"
              bg={styles.pillTones.neutral.bg}
              color={styles.pillTones.neutral.color}
            >
              {isAddressed ? <Icon as={FiLock} boxSize={2.5} /> : null}
              {isAddressed ? 'Addressed' : 'Open'}
            </Badge>
          </Flex>

          {metaItems.length > 0 ? (
            <Flex
              as="p"
              fontSize="sm"
              color={subtleText}
              align="center"
              flexWrap="wrap"
              columnGap={1.5}
              rowGap={0.5}
              lineHeight="1.35"
            >
              {metaItems.map((item, index) => (
                <Box
                  as="span"
                  key={item.key}
                  display="inline-flex"
                  alignItems="center"
                  gap={1.5}
                  minW={0}
                >
                  {index > 0 ? (
                    <Box as="span" aria-hidden="true">
                      •
                    </Box>
                  ) : null}
                  <Box
                    as="span"
                    display="inline-flex"
                    alignItems="center"
                    gap={1}
                    minW={0}
                  >
                    {item.icon ? (
                      <Box
                        as={item.icon}
                        boxSize="13px"
                        aria-hidden="true"
                        flexShrink={0}
                      />
                    ) : null}
                    <Box as="span" minW={0}>
                      {item.label}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Flex>
          ) : null}

          {preview ? (
            <Text
              fontSize="sm"
              color={captionTextColor}
              lineHeight="1.45"
              noOfLines={2}
              mt={0.5}
            >
              {preview}
            </Text>
          ) : null}
        </VStack>

        <Flex
          align="center"
          gap={1}
          flexShrink={0}
          color={isPending ? styles.accentColor : subtleText}
          alignSelf="center"
          fontSize="sm"
          fontWeight="medium"
        >
          <Box as="span" display={{base: 'none', sm: 'inline'}}>
            {isPending ? 'Opening…' : 'Edit'}
          </Box>
          <Icon
            as={isPending ? FiArrowRight : FiChevronRight}
            boxSize={4}
            aria-hidden="true"
          />
        </Flex>
      </Flex>
    </Box>
  );
};

// The open-telephone-encounter picker shown in CREATE mode once the chosen
// patient already has one or more open telephone encounters in the EHR. It
// replaces the old single-match prompt (which only ever let you edit the first
// match) with a proper, selectable list so the registrar can see every open
// encounter — reason, provider, assignee, who opened it, and a preview of the
// message — and pick exactly the one they want to continue, or start fresh.
const OpenTelephoneEncounterPicker = ({
  patientName,
  matches,
  onEdit,
  onCreateNew,
}: {
  patientName?: string | null;
  matches: OpenTelephoneEncounterSummary[];
  onEdit: (match: OpenTelephoneEncounterSummary) => void;
  onCreateNew: () => void;
}) => {
  const {subtleText} = usePlannerStyles();
  // Track which row was clicked so we can show an inline "Opening…" state on
  // exactly that card while the parent adopts the encounter.
  const [pendingId, setPendingId] = useState<string | null>(null);

  const count = matches.length;
  const who = String(patientName || '').trim() || 'This patient';
  const intro =
    count > 1
      ? `${who} already has ${count} open telephone encounters. Pick the one you want to continue — or start a new one.`
      : `${who} already has an open telephone encounter. Continue it to keep everything on one record — or start a new one.`;

  const handleEdit = (match: OpenTelephoneEncounterSummary) => {
    if (pendingId) return;
    setPendingId(match.id);
    onEdit(match);
  };

  return (
    <VStack align="stretch" spacing={3}>
      <Text fontSize="sm" color={subtleText} lineHeight="1.45">
        {intro}
      </Text>

      <VStack align="stretch" spacing={2.5}>
        {matches.map(match => (
          <OpenEncounterCard
            key={match.id}
            match={match}
            isBusy={Boolean(pendingId)}
            isPending={pendingId === match.id}
            onEdit={() => handleEdit(match)}
          />
        ))}
      </VStack>

      <Flex
        align="center"
        gap={3}
        pt={1}
        direction={{base: 'column', sm: 'row'}}
      >
        <Text fontSize="sm" color={subtleText} flex={1} minW={0}>
          None of these match this call?
        </Text>
        <Button
          {...RECAP_GRAY_SECONDARY_BUTTON_PROPS}
          size="sm"
          h="36px"
          minH="36px"
          fontSize="sm"
          w={{base: '100%', sm: 'auto'}}
          leftIcon={<FiPlus size={15} aria-hidden="true" />}
          isDisabled={Boolean(pendingId)}
          onClick={onCreateNew}
        >
          Start a new encounter
        </Button>
      </Flex>

      <Box srOnly aria-live="polite">
        {pendingId ? `Opening telephone encounter for ${who}` : ''}
      </Box>
    </VStack>
  );
};

export default OpenTelephoneEncounterPicker;
